import {
    CommonModule,
    CurrencyPipe,
    DatePipe,
    DecimalPipe,
    I18nPluralPipe,
    PercentPipe,
    TitleCasePipe
} from '@angular/common';
import { HttpClientModule } from '@angular/common/http';
import { ModuleWithProviders, NgModule } from '@angular/core';
import { MATERIAL_SANITY_CHECKS } from '@angular/material/core';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { RouterModule } from '@angular/router';
import { WINDOW } from '@lsq-frontend/angular-shared/settings';
import { AngularSvgIconModule } from 'angular-svg-icon';
import { ClickOutsideModule } from 'ng-click-outside';
import { DeviceDetectorModule } from 'ngx-device-detector';
import { IConfig, NgxMaskModule } from 'ngx-mask';
import { ScrollbarModule } from 'ngx-scrollbar';
import { PopoverModule } from 'ngx-smart-popover';
import { LsqAppRootComponent } from './components/containers/app-root/lsq-app-root.component';
import { LsqFooterComponent } from './components/containers/footer/lsq-footer.component';
import { LsqNoopComponent } from './components/containers/noop/lsq-noop.component';
import { LsqSessionIdleModalComponent } from './components/modals/session-idle-modal.component';
import { LsqNotFoundComponent } from './components/pages/not-found/lsq-not-found.component';
import { LsqUnavailableComponent } from './components/pages/unavailable/lsq-unavailable.component';
import { LsqToastComponent } from './components/toast/lsq-toast.component';
import { LsqActionSheetComponent } from './components/ui-elements/action-sheet/lsq-action-sheet.component';
import { LsqAddressComponent } from './components/ui-elements/address/lsq-address.component';
import { AnnouncementRowComponent } from './components/ui-elements/announcement/lsq-announcement-row.component';
import {
    AnnouncementHeight,
    LsqAnnouncementComponent
} from './components/ui-elements/announcement/lsq-announcement.component';
import { LsqBackdropComponent } from './components/ui-elements/backdrop/lsq-backdrop.component';
import { LsqBaseNavigationComponent } from './components/ui-elements/base-navigation/lsq-base-navigation.component';
import { LsqButtonComponent } from './components/ui-elements/button/lsq-button.component';
import { LsqDropdownNavigationComponent } from './components/ui-elements/dropdown-navigation/lsq-dropdown-navigation.component';
import { LsqFloatingActionButtonComponent } from './components/ui-elements/floating-action-button/lsq-floating-action-button.component';
import { LsqIconPopoverComponent } from './components/ui-elements/icon-popover/lsq-icon-popover.component';
import { LsqIconComponent } from './components/ui-elements/icon/lsq-icon.component';
import { LsqImagePreviewComponent } from './components/ui-elements/image-preview/lsq-image-preview.component';
import { LsqButtonSpinnerComponent } from './components/ui-elements/loaders/button-spinner/lsq-button-spinner.component';
import { LsqCircleLoaderComponent } from './components/ui-elements/loaders/circle-page-loader/lsq-circle-loader.component';
import { LsqMobileNavigationComponent } from './components/ui-elements/mobile-navigation/lsq-mobile-navigation.component';
import { LsqMobileNavigationItemComponent } from './components/ui-elements/mobile-navigation/mobile-navigation-item/lsq-mobile-navigation-item.component';
import { LsqNavigationComponent } from './components/ui-elements/navigation/lsq-navigation.component';
import { LsqSmartTruncateComponent } from './components/ui-elements/smart-truncate/lsq-smart-truncate.component';
import { LsqTruncateComponent } from './components/ui-elements/truncate/lsq-truncate.component';
import { ClaimAndFeatureFlagDirective } from './directives/claim-and-feature-flag.directive';
import { ClaimDirective } from './directives/claim.directive';
import { ClaimsOrDirective } from './directives/claims-or.directive';
import { DelayRenderDirective } from './directives/delay-render.directive';
import { EnvironmentsDirective } from './directives/environments.directive';
import { FeatureFlagDirective } from './directives/feature-flag.directive';
import { IosNestedScrollDirective } from './directives/ios-nested-scroll.directive';
import { AddressPipe } from './pipes/address.pipe';
import { ArraySortPipe } from './pipes/array-sort.pipe';
import { BpsToPercentPipe } from './pipes/bps-to-percent.pipe';
import { CacheValuePipe } from './pipes/cache-value.pipe';
import { CurrencyDashPipe } from './pipes/currency-dash.pipe';
import { LsqCurrencyPipe } from './pipes/currency.pipe';
import { DateFnsPipe } from './pipes/date-fns.pipe';
import { DaysPassedPipe } from './pipes/days-passed.pipe';
import { DaysPipe } from './pipes/days.pipe';
import { FilterArrayPipe } from './pipes/filter-array.pipe';
import { GetOffsetIndexOfSiblingRowPipe } from './pipes/get-offset-index-of-sibling-row.pipe';
import { IterablePipe } from './pipes/iterable.pipe';
import { LastUpdatedDatePipe } from './pipes/last-updated-date.pipe';
import { LsqDatePipe } from './pipes/lsq-date.pipe';
import { NotNullPipe } from './pipes/not-null.pipe';
import { LsqPercentPipe } from './pipes/percent.pipe';
import { PhonePipe } from './pipes/phone.pipe';
import { RedactedBankAccountPipe } from './pipes/redacted-bank-account.pipe';
import { ReversePipe } from './pipes/reverse.pipe';
import { RouteParamsPipe } from './pipes/route-params.pipe';
import { SafeHtmlPipe } from './pipes/safe-html.pipe';
import { SerializedDatePipe } from './pipes/serialized-date.pipe';
import { TruncateTextPipe } from './pipes/truncate-text.pipe';
import { RouterLinkActiveOrNext } from './routing/next-route/router-link-active-or-next.directive';
import { ActionSheetService } from './services/action-sheet/action-sheet.service';
import { Announcement, AnnouncementService } from './services/announcement/announcement.service';
import { BackdropService } from './services/backdrop/backdrop.service';
import { ClaimService } from './services/claim/claim.service';
import { DetectBrowserService } from './services/detect-browser/detect-browser.service';
import { FeatureFlagService } from './services/feature-flag/feature-flag.service';
import { FloatingActionButtonService } from './services/floating-action-button/floating-action-button.service';
import { HideOpenElementsService } from './services/hide-open-elements/hide-open-elements.service';
import { IdleService } from './services/idle/idle.service';
import { ResponsiveService } from './services/responsive/responsive.service';
import { GetPayLaterProgramService } from './services/pay-later/get-pay-later-program.service';
import { Serializer } from './services/serializer/serializer';
import { SessionService } from './services/session/session.service';
import { WindowReferenceService } from './services/window-reference/window-reference.service';
import { WorkflowMonitorService } from './services/workflow-monitor/workflow-monitor.service';
import { DisableControlDirective } from './utilities/disableFromControl.directive';
import { ErrorMessageHelper } from './utilities/error-message.i18n.helper';

const maskConfig: Partial<IConfig> = {
    validation: false
};

export * from './constants/icons.constants';
export * from './routing/atlas/atlas';
export * from './routing/atlas/atlas.helpers';
export * from './utilities/a11y.helpers';
export {
    LsqAppRootComponent,
    LsqAnnouncementComponent,
    AnnouncementRowComponent,
    LsqSessionIdleModalComponent,
    LsqToastComponent,
    Announcement,
    AnnouncementService,
    ErrorMessageHelper,
    AnnouncementHeight,
    ClaimService,
    ClaimAndFeatureFlagDirective,
    DisableControlDirective,
    ClaimDirective,
    ClaimsOrDirective,
    EnvironmentsDirective,
    FeatureFlagDirective,
    DelayRenderDirective,
    DetectBrowserService,
    FeatureFlagService,
    FloatingActionButtonService,
    HideOpenElementsService,
    IdleService,
    SessionService,
    IosNestedScrollDirective,
    ResponsiveService,
    GetPayLaterProgramService,
    Serializer,
    WindowReferenceService,
    WorkflowMonitorService,
    LsqCircleLoaderComponent,
    LsqImagePreviewComponent,
    LsqBaseNavigationComponent,
    LsqNavigationComponent,
    LsqDropdownNavigationComponent,
    LsqUnavailableComponent,
    LsqBackdropComponent,
    BackdropService,
    LsqFloatingActionButtonComponent,
    LsqAddressComponent,
    LsqSmartTruncateComponent,
    LsqTruncateComponent,
    LsqIconComponent,
    LsqIconPopoverComponent,
    LsqButtonComponent,
    LsqButtonSpinnerComponent,
    LsqNoopComponent,
    LsqFooterComponent,
    LsqNotFoundComponent,
    AddressPipe,
    ArraySortPipe,
    BpsToPercentPipe,
    CacheValuePipe,
    CurrencyDashPipe,
    LsqCurrencyPipe,
    DateFnsPipe,
    DaysPipe,
    DaysPassedPipe,
    FilterArrayPipe,
    GetOffsetIndexOfSiblingRowPipe,
    IterablePipe,
    LastUpdatedDatePipe,
    LsqDatePipe,
    NotNullPipe,
    LsqPercentPipe,
    PhonePipe,
    RedactedBankAccountPipe,
    ReversePipe,
    RouteParamsPipe,
    SafeHtmlPipe,
    SerializedDatePipe,
    TruncateTextPipe,
    LsqActionSheetComponent,
    ActionSheetService,
    LsqMobileNavigationItemComponent,
    LsqMobileNavigationComponent
};

@NgModule({
    imports: [
        AngularSvgIconModule,
        ClickOutsideModule,
        CommonModule,
        DeviceDetectorModule.forRoot(),
        HttpClientModule,
        MatProgressSpinnerModule,
        PopoverModule,
        RouterModule,
        ScrollbarModule,
        NgxMaskModule.forRoot(maskConfig)
    ],
    declarations: [
        AddressPipe,
        AnnouncementRowComponent,
        ArraySortPipe,
        BpsToPercentPipe,
        CacheValuePipe,
        ClaimAndFeatureFlagDirective,
        DisableControlDirective,
        ClaimDirective,
        ClaimsOrDirective,
        CurrencyDashPipe,
        DaysPipe,
        DaysPassedPipe,
        DelayRenderDirective,
        EnvironmentsDirective,
        IosNestedScrollDirective,
        FeatureFlagDirective,
        FilterArrayPipe,
        GetOffsetIndexOfSiblingRowPipe,
        IterablePipe,
        LastUpdatedDatePipe,
        LsqDatePipe,
        NotNullPipe,
        LsqActionSheetComponent,
        LsqAddressComponent,
        LsqAnnouncementComponent,
        LsqAppRootComponent,
        LsqBackdropComponent,
        LsqBaseNavigationComponent,
        LsqButtonComponent,
        LsqButtonSpinnerComponent,
        LsqCircleLoaderComponent,
        LsqCurrencyPipe,
        DateFnsPipe,
        LsqDropdownNavigationComponent,
        LsqFloatingActionButtonComponent,
        LsqFooterComponent,
        LsqIconComponent,
        LsqIconPopoverComponent,
        LsqImagePreviewComponent,
        LsqMobileNavigationComponent,
        LsqMobileNavigationItemComponent,
        LsqNavigationComponent,
        LsqNoopComponent,
        LsqNotFoundComponent,
        LsqSessionIdleModalComponent,
        LsqToastComponent,
        LsqSmartTruncateComponent,
        LsqTruncateComponent,
        LsqUnavailableComponent,
        LsqPercentPipe,
        PhonePipe,
        RedactedBankAccountPipe,
        ReversePipe,
        RouteParamsPipe,
        SafeHtmlPipe,
        SerializedDatePipe,
        TruncateTextPipe,
        RouterLinkActiveOrNext
    ],
    exports: [
        AddressPipe,
        AngularSvgIconModule,
        AnnouncementRowComponent,
        ArraySortPipe,
        BpsToPercentPipe,
        CacheValuePipe,
        ClaimAndFeatureFlagDirective,
        DisableControlDirective,
        ClaimDirective,
        ClaimsOrDirective,
        ClickOutsideModule,
        CurrencyDashPipe,
        DaysPipe,
        DaysPassedPipe,
        DelayRenderDirective,
        DeviceDetectorModule,
        EnvironmentsDirective,
        IosNestedScrollDirective,
        FeatureFlagDirective,
        FilterArrayPipe,
        GetOffsetIndexOfSiblingRowPipe,
        HttpClientModule,
        IterablePipe,
        LastUpdatedDatePipe,
        LsqDatePipe,
        NotNullPipe,
        LsqActionSheetComponent,
        LsqAddressComponent,
        LsqAnnouncementComponent,
        LsqAppRootComponent,
        LsqBackdropComponent,
        LsqBaseNavigationComponent,
        LsqButtonComponent,
        LsqButtonSpinnerComponent,
        LsqCircleLoaderComponent,
        LsqCurrencyPipe,
        DateFnsPipe,
        LsqDropdownNavigationComponent,
        LsqFloatingActionButtonComponent,
        LsqFooterComponent,
        LsqIconComponent,
        LsqIconPopoverComponent,
        LsqImagePreviewComponent,
        LsqMobileNavigationComponent,
        LsqNavigationComponent,
        LsqNoopComponent,
        LsqNotFoundComponent,
        LsqSessionIdleModalComponent,
        LsqToastComponent,
        LsqSmartTruncateComponent,
        LsqTruncateComponent,
        LsqUnavailableComponent,
        MatProgressSpinnerModule,
        SerializedDatePipe,
        TruncateTextPipe,
        LsqPercentPipe,
        PhonePipe,
        PopoverModule,
        RedactedBankAccountPipe,
        ReversePipe,
        RouteParamsPipe,
        RouterModule,
        SafeHtmlPipe,
        ScrollbarModule,
        RouterLinkActiveOrNext
    ]
})
export class LsqCoreModule {
    public static forRoot(): ModuleWithProviders<LsqCoreModule> {
        console.debug('Loading libs/LsqCoreModule.forRoot.');
        return {
            ngModule: LsqCoreModule,
            providers: [
                AddressPipe,
                ArraySortPipe,
                BpsToPercentPipe,
                CacheValuePipe,
                CurrencyDashPipe,
                CurrencyPipe,
                DatePipe,
                DaysPipe,
                DaysPassedPipe,
                DecimalPipe,
                FilterArrayPipe,
                GetOffsetIndexOfSiblingRowPipe,
                I18nPluralPipe,
                IterablePipe,
                LastUpdatedDatePipe,
                LsqDatePipe,
                LsqCurrencyPipe,
                DateFnsPipe,
                SerializedDatePipe,
                TruncateTextPipe,
                PercentPipe,
                LsqPercentPipe,
                PhonePipe,
                RedactedBankAccountPipe,
                ReversePipe,
                RouteParamsPipe,
                SafeHtmlPipe,
                TitleCasePipe,
                {
                    provide: MATERIAL_SANITY_CHECKS,
                    useValue: false
                },
                {
                    provide: WINDOW,
                    useValue: window
                }
            ]
        };
    }

    public static forChild(): ModuleWithProviders<LsqCoreModule> {
        console.debug('Loading libs/LsqCoreModule.forChild.');
        return {
            ngModule: LsqCoreModule
        };
    }
}
